import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { ErrorResponse, TenantDomain } from '@clientwire/api';
import { WireApiService } from '@frontend/shared';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { GeneralService } from '../general.service';

@Injectable({ providedIn: 'root' })
export class DomainDialogService {
  readonly #wireApiService = inject(WireApiService);
  readonly #translateService = inject(TranslateService);
  readonly #generalService = inject(GeneralService);

  readonly #toastr = inject(ToastrService);

  readonly domains: WritableSignal<TenantDomain[]> = signal([]);
  readonly domainsLoading = signal(false);
  readonly tenant = this.#generalService.tenant;


  loadDomains(): void {
    const tenantValue = this.tenant();
    if (!tenantValue?.id) return;

    this.domainsLoading.set(true);
    this.#wireApiService.tenantDomainsApi
      .getDomains({ tenantId: tenantValue.id })
      .then((response) => {
        this.domains.set(response);
      })
      .catch(this.handleError('ERROR_LOADING'))
      .finally(() => {
        this.domainsLoading.set(false);
      });
  }

  createCustomDomain(domain: string, isClientApp: boolean): Promise<TenantDomain> {
    const tenantValue = this.tenant();
    if (!tenantValue?.id) {
      return Promise.reject(new Error('Tenant not found'));
    }

    this.domainsLoading.set(true);
    return this.#wireApiService.tenantDomainsApi
      .createDomain({
        tenantId: tenantValue.id,
        tenantDomainCreate: {
          domain: domain,
          isClientApp: isClientApp
        },
      })
      .then((response) => {
        // Reload domains after successful creation
        this.loadDomains();
        this.#toastr.success(
          this.#translateService.instant('SETTINGS.GENERAL.DOMAINS.CREATED_MESSAGE'),
          this.#translateService.instant('SETTINGS.GENERAL.DOMAINS.CREATED_TITLE')
        );
        return response;
      })
      .catch((error) => {
        this.handleError('ERROR_CREATING')(error);
        throw error;
      })
      .finally(() => {
        this.domainsLoading.set(false);
      });
  }

  updateCustomDomain(domainId: string, domain: string): Promise<TenantDomain> {
    const tenantValue = this.tenant();
    if (!tenantValue?.id) {
      return Promise.reject(new Error('Tenant not found'));
    }

    this.domainsLoading.set(true);
    return this.#wireApiService.tenantDomainsApi
      .updateDomain({
        tenantId: tenantValue.id,
        domainId: domainId,
        tenantDomainUpdate: {
          domain: domain
        }
      })
      .then((response) => {
        // Reload domains after successful update
        this.loadDomains();
        this.#toastr.success(
          this.#translateService.instant('SETTINGS.GENERAL.DOMAINS.UPDATED_MESSAGE'),
          this.#translateService.instant('SETTINGS.GENERAL.DOMAINS.UPDATED_TITLE')
        );
        return response;
      })
      .catch((error) => {
        this.handleError('ERROR_UPDATING')(error);
        throw error;
      })
      .finally(() => {
        this.domainsLoading.set(false);
      });
  }

  deleteCustomDomain(domainId: string): Promise<void> {
    const tenantValue = this.tenant();
    if (!tenantValue?.id) {
      return Promise.reject(new Error('Tenant not found'));
    }

    this.domainsLoading.set(true);
    return this.#wireApiService.tenantDomainsApi
      .deleteDomain({
        tenantId: tenantValue.id,
        domainId
      })
      .then(() => {
        // Reload domains after successful deletion
        this.loadDomains();
        this.#toastr.success(
          this.#translateService.instant('SETTINGS.GENERAL.DOMAINS.DELETED_MESSAGE'),
          this.#translateService.instant('SETTINGS.GENERAL.DOMAINS.DELETED_TITLE')
        );
      })
      .catch((error) => {
        this.handleError('ERROR_DELETING')(error);
        throw error;
      })
      .finally(() => {
        this.domainsLoading.set(false);
      });
  }

  getSystemDomains(): TenantDomain[] {
    return this.domains().filter(domain => domain.isSystemDomain);
  }

  getCustomDomains(): TenantDomain[] {
    return this.domains().filter(domain => !domain.isSystemDomain);
  }

  getCustomDomainByType(isClientApp: boolean): TenantDomain | undefined {
    return this.getCustomDomains().find(domain => domain.isClientApp === isClientApp);
  }


  private handleError(errorType: string) {
    return async (error: any) => {
      console.error(`Error in tenant operation (${errorType}):`, error);

      let errorMessage = this.#translateService.instant(`SETTINGS.GENERAL.DOMAINS.${errorType}_MESSAGE`);
      try {
        if (error.response) {
          const errorData: ErrorResponse = await error.response.json();
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        }
      } catch (e) {
        console.error('Error parsing error response:', e);
      }

      this.#toastr.error(
        errorMessage,
        this.#translateService.instant(`SETTINGS.GENERAL.DOMAINS.${errorType}_TITLE`)
      );
    };
  }
}
